import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Appointment } from 'src/app/core/models/appointment.model';
// tslint:disable-next-line: max-line-length
import { ClearCodeOption, ClearCodeState, ClearCodeType } from 'src/app/core/models/ticket-clear-code-type.model';
import { AppointmentService } from 'src/app/core/services/appointment/appointment.service';
import { ClearCodeService } from 'src/app/core/services/clear-code/clear-code.service';
import { LocalStorageService } from 'src/app/core/storage/local-storage/local-storage.service';
import { SyncService } from 'src/app/core/sync/sync.service';

@Component({
     selector: 'app-codes-tab',
     templateUrl: './codes-tab.component.html',
     styleUrls: ['./codes-tab.component.scss'],
})
export class CodesTabComponent implements OnInit, OnDestroy {

     isLoaded: boolean;
     appointment: Appointment;
     codes: ClearCodeState[];
     clearCodeTypes = [] as ClearCodeType[];
     dropdownOptions = [] as Array<ClearCodeOption[]>;
     destroyed$: Subject<boolean>;

     constructor(
          private activatedRoute: ActivatedRoute,
          private clearCodeService: ClearCodeService,
          private storage: LocalStorageService,
          private syncService: SyncService,
          private appointmentService: AppointmentService,
     ) { }

     async ngOnInit() {
          this.isLoaded = false;
          this.destroyed$ = new Subject();

          if (!this.isLoaded) {
               const id = this.activatedRoute.snapshot.parent.parent.params?.id;
               const appointment = await this.storage.appointments.getItem<Appointment>(id);
               if (!appointment) {
                    return;
               }

               await this.initialSetup(appointment);

               this.isLoaded = true;
          }

          // Reset on reload configuration
          this.syncService.isBlockingSync$
               .pipe(takeUntil(this.destroyed$))
               .subscribe(isBlocking => {
                    if (isBlocking) {
                         this.clearCodeService.clearTable();
                    }
               });
     }

     ngOnDestroy() {
          this.destroyed$.next(true);
          this.destroyed$.complete();
     }

     private async initialSetup(appointment: Appointment): Promise<void> {
          this.appointment = appointment;
          this.codes = [];

          if (!await this.loadClearCodeTypes(appointment.ticketCodeId)) {
               return;
          }

          await this.loadOptions(this.clearCodeTypes[0].cctId, 0);
          await this.loadSavedState(this.appointment.id);
     }

     private async loadClearCodeTypes(ticketCodeId: number): Promise<boolean> {
          const ccTypes = await this.clearCodeService.getTicketClearCodeTypes(ticketCodeId);
          if (!ccTypes) {
               return false;
          }

          this.clearCodeTypes = ccTypes.types.sort((a, b) => a.order - b.order);

          return true;
     }

     private async loadSavedState(appointmentId: number): Promise<void> {
          const state = await this.clearCodeService.getAppointmentClearCode(appointmentId);
          if (!state) {
               return;
          }

          // If there is a descendant dropdown, load its options
          await Promise.all(state.clearCode.codes.map(async (entry, index) => {
               if (this.clearCodeTypes[index + 1]) {
                    const availableOptions = entry.ticketClearCodeOption?.childOptions;
                    if (availableOptions) {
                         await this.loadNextOptions(index, availableOptions);
                    }
               }
          }));
          this.codes = state.clearCode.codes;
     }

     async loadOptions(cctid: number, index: number, availableOptions?: number[]): Promise<void> {
          let options = await this.clearCodeService.getTicketClearCodeOptions(cctid);

          if (availableOptions) {
               options = options.filter(option => availableOptions.includes(option.id));
          }

          const allowedIds = await this.clearCodeService.getAllowedClearCodeIds(this.appointment.ticketCodeId);
          if (allowedIds) {
               options = options.filter(option => allowedIds.includes(option.id));
          }


          options.sort((a, b) => (a.desc < b.desc) ? -1 : (a.desc > b.desc) ? 1 : 0);

          this.dropdownOptions.splice(index, 1, options);
     }

     async loadNextOptions(dropdownIndex: number, availableOptions: number[]): Promise<void> {
          // If there are no more dropdowns to populate, exit
          const nextIndex = dropdownIndex + 1;
          if (!this.clearCodeTypes[nextIndex]) {
               return;
          }

          const nextCCTID = this.clearCodeTypes[nextIndex].cctId;
          await this.loadOptions(nextCCTID, nextIndex, availableOptions);
     }

     async onChangeUpdateSelect(event: CustomEvent, dropdownIndex: number): Promise<void> {
          if (event.detail.value == null) { // Ignore reset values or non-selected values
               return;
          }

          await this.update(event, dropdownIndex);
     }

     private async update(event: CustomEvent, dropdownIndex: number): Promise<void> {
          await this.changeState(event, dropdownIndex);

          this.removeDropdownOptionsAfter(dropdownIndex);

          await this.loadNextOptions(dropdownIndex, event.detail.value?.childOptions);
     }

     removeDropdownOptionsAfter(dropdownIndex: number) {
          this.dropdownOptions = this.dropdownOptions.slice(0, dropdownIndex + 1);
     }

     async changeState(event: CustomEvent, dropdownIndex: number): Promise<void> {
          this.setState(event, dropdownIndex);
          await this.saveState(this.codes);
     }

     // Update component state
     private setState(event: CustomEvent, dropdownIndex: number): void {
          const entry = {
               ticketClearCodeType: this.clearCodeTypes[dropdownIndex],
               ticketClearCodeOption: event.detail.value,
          } as ClearCodeState;

          // Remove entries after newest
          const tmpCodes = this.codes.slice(0, dropdownIndex + 1);

          // Update with newest entry
          tmpCodes.splice(dropdownIndex, 1, entry);

          // Set state
          this.codes = tmpCodes;
     }

     // Save state to indexeddb
     private async saveState(codes: ClearCodeState[]): Promise<void> {
          const state = await this.appointmentService.getClearCodeState(this.appointment.id);

          state.clearCode.codes = codes;

          await this.storage.appointmentClearCode.setItem(`${this.appointment.id}`, state);
     }

     hasOptions(index: number): boolean {
          return (!!this.dropdownOptions[index]);
     }

     compareWith(o1, o2): boolean {
          return o1 && o2 ? o1.id === o2.id : o1 === o2;
     }

}
